import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { ResultPage } from '../../../../components/Learner/ResultPage';
// @ts-ignore - TypeScript casing issue
import {
  levelStateAtom,
  speakingAudioRecordingsState,
  lessonsBySubcourseAtom,
  courseTreeAtom,
  courseTreeBaselineAtom,
} from '../../../../recoil/atoms';
import { getLastOpenedUrl } from '../../../../utils/lastOpened';
import { getResultPracticeContext } from '../../../../utils/practiceUtils';

// Wrapper component for route
const ResultPageRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const setLevelState = useSetRecoilState(levelStateAtom);
  // changed by Cursor: read audio from Recoil instead of localStorage
  const audioRecordings = useRecoilValue(speakingAudioRecordingsState);
  const lessonsCache = useRecoilValue(lessonsBySubcourseAtom);
  const courseTree = useRecoilValue(courseTreeAtom);
  const courseTreeBaseline = useRecoilValue(courseTreeBaselineAtom);
  
  // Get result data from location state
  // If no location.state, redirect to language learning page (no mockup data)
  const locationState = location.state;
  
  // Extract data safely (will be null if no locationState)
  const {
    language: propLanguage,
    courseType = 'Course Practice',
    level = 'All Levels',
    currentLevel: propCurrentLevel,
    previousLevel: propPreviousLevel,
    deltaLevel: propDeltaLevel,
    score: propScore,
    totalQuestions: propTotalQuestions,
    correctCount: propCorrectCount,
    practiceParams = null,
    apiResponse = null, // Store full API response for debugging
    originalQuestions = [], // Original questions with options from GET practice
    userAnswers = [], // User answers array (separate from soals-stats)
    sessionData = null, // Session data from GET practice
    fromPage = '/learner/language-learning', // Track where user came from
  } = locationState || {};
  
  // Determine language based on propLanguage, sessionData, or fromPage
  let language = propLanguage || sessionData?.['course-name'] || null;
  
  // If not found, infer from fromPage
  if (!language) {
    if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
      language = 'English language learning';
    } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
      language = 'Japanese language learning';
    } else {
      language = 'German language learning';
    }
  }
  
  // Redirect if no data from practice (must be after all hooks)
  // Allow navigation even if apiResponse is null, as long as we have minimal data (score, totalQuestions, etc)
  useEffect(() => {
    if (!locationState) {
      // No location state at all, redirect to language learning page
      navigate('/learner/language-learning', { replace: true });
      return;
    }
    
    // Check if we have minimal required data (either apiResponse OR fallback data)
    const hasApiResponse = !!locationState.apiResponse;
    const hasFallbackData = locationState.score !== undefined || 
                            locationState.totalQuestions !== undefined ||
                            locationState.correctCount !== undefined;
    
    if (!hasApiResponse && !hasFallbackData) {
      // No practice data at all, redirect to language learning page
      navigate('/learner/language-learning', { replace: true });
    }
  }, [locationState, navigate]);

  // Extract data from API response if available
  // API response structure:
  // {
  //   "to-display": {
  //     "session-accuracy": 38,
  //     "level": 3,
  //     "progress": 89,
  //     "old-level": 3,
  //     "old-progress": 84,
  //     "delta-progress": 5
  //   }
  // }
  const toDisplay = apiResponse?.['to-display'] || apiResponse?.toDisplay || {};
  
  // Check if to-display has the required fields
  const hasApiData = toDisplay && 
    typeof toDisplay === 'object' && 
    (toDisplay['session-accuracy'] !== undefined || 
     toDisplay['level'] !== undefined || 
     toDisplay['old-level'] !== undefined);
  
  // Extract from API response if available, otherwise use props
  let score, previousLevel, currentLevel, deltaLevel;
  
  if (hasApiData) {
    // Use API response data directly from to-display
    // Score: use session-accuracy directly
    score = toDisplay['session-accuracy'] ?? toDisplay.sessionAccuracy ?? propScore ?? 0;
    
    // Level: use level + progress/100 for XPBar format (e.g., 3 + 89/100 = 3.89)
    const apiLevel = toDisplay['level'] ?? toDisplay.level ?? propCurrentLevel ?? 2;
    const apiProgress = toDisplay['progress'] ?? toDisplay.progress ?? 0;
    currentLevel = apiLevel + (apiProgress / 100);
    
    // Previous level: use old-level + old-progress/100 for XPBar format
    const apiOldLevel = toDisplay['old-level'] ?? toDisplay.oldLevel ?? propPreviousLevel ?? 2;
    const apiOldProgress = toDisplay['old-progress'] ?? toDisplay.oldProgress ?? 0;
    previousLevel = apiOldLevel + (apiOldProgress / 100);
    
    // Delta level: use delta-progress/100 for XPBar format (e.g., 5/100 = 0.05)
    const apiDeltaProgress = toDisplay['delta-progress'] ?? toDisplay.deltaProgress ?? propDeltaLevel ?? 0;
    deltaLevel = apiDeltaProgress / 100;
    
    console.log('✅ Using API data from to-display:', { 
      score, 
      previousLevel, 
      currentLevel, 
      deltaLevel,
      'Raw values from backend': {
        'session-accuracy': toDisplay['session-accuracy'],
        'level': apiLevel,
        'progress': apiProgress,
        'old-level': apiOldLevel,
        'old-progress': apiOldProgress,
        'delta-progress': apiDeltaProgress,
      }
    });
  } else {
    // Use props from location.state (fallback)
    score = propScore ?? 8;
    previousLevel = propPreviousLevel ?? 1.8;
    currentLevel = propCurrentLevel ?? 2;
    deltaLevel = propDeltaLevel ?? 0.2;
    console.log('⚠️ Using fallback props:', { score, previousLevel, currentLevel, deltaLevel });
  }
  
  // For score display, get totalQuestions and correctCount
  // If API has to-display with soals-stats, use its length for totalQuestions
  let totalQuestions = propTotalQuestions ?? 10;
  let correctCount = propCorrectCount ?? 0;
  
  if (hasApiData && toDisplay['soals-stats']) {
    // Get totalQuestions from soals-stats array length
    totalQuestions = toDisplay['soals-stats'].length;
    // Calculate correctCount from session-accuracy percentage
    correctCount = Math.round((score / 100) * totalQuestions);
  } else if (hasApiData) {
    // If no soals-stats but has score, use default totalQuestions
    correctCount = Math.round((score / 100) * totalQuestions);
  }

  // Initialize level state for Level component animation
  useEffect(() => {
    const oldLevelInt = Math.floor(previousLevel);
    setLevelState({
      currentDisplayLevelForPulse: oldLevelInt,
      isLevelChanging: false,
      levelChangeDirection: 'none',
    });
  }, [previousLevel, setLevelState]);

  // changed by Cursor: vocab drill uses blue background, others use green
  const isDrillVocab = courseType === 'Drill Vocab';
  useEffect(() => {
    const bg = isDrillVocab
      ? 'linear-gradient(135deg, #1e3d6f 0%, #345d8a 50%, #4a6fa5 100%)'
      : 'linear-gradient(135deg, #1d4746 0%, #236463 50%, #12766d 100%)';
    document.body.style.background = bg;
    document.body.style.backgroundImage = 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 1px, transparent 1px)';
    document.body.style.backgroundSize = '24px 24px';
    document.documentElement.style.background = bg;
    
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundImage = '';
      document.body.style.backgroundSize = '';
      document.documentElement.style.background = '';
    };
  }, [isDrillVocab]);

  // Log API response for debugging
  useEffect(() => {
    if (apiResponse) {
      console.log('📊 Result Page - API Response Data:', apiResponse);
      console.log('📊 Result Page - Extracted Values:', {
        score,
        previousLevel,
        currentLevel,
        deltaLevel,
      });
    }
  }, [apiResponse, previousLevel, currentLevel, deltaLevel, score]);

  const handleBack = () => {
    // Use practiceParams.lessonId only - sessionData may have lesson-id from API even for subcourse-level practice.
    // Only go to lesson tab when user explicitly started from a lesson (practiceParams.lessonId).
    const lessonId = practiceParams?.lessonId;
    const subcourseId = practiceParams?.subcourseId || sessionData?.['subcourse-id'];
    
    // If practice was started from a lesson, build URL to return to that lesson table
    // But check if fromPage is English Language Learning Page, use that instead
    if (lessonId && subcourseId) {
      const basePath = fromPage?.split('?')[0];
      if (basePath?.includes('/learner/english-language-learning')) {
        const lessonTableUrl = `${basePath}?subcourse-id=${subcourseId}&lesson-id=${lessonId}`;
        console.log('📖 ResultPage: Navigating back to English lesson table:', lessonTableUrl);
        navigate(lessonTableUrl);
        return;
      }
      if (basePath?.includes('/learner/japanese-language-learning')) {
        const lessonTableUrl = `${basePath}?subcourse-id=${subcourseId}&lesson-id=${lessonId}`;
        console.log('📖 ResultPage: Navigating back to Japanese lesson table:', lessonTableUrl);
        navigate(lessonTableUrl);
        return;
      }
      const lessonTableUrl = `/learner/language-learning?subcourse-id=${subcourseId}&lesson-id=${lessonId}`;
      console.log('📖 ResultPage: Navigating back to lesson table:', lessonTableUrl);
      navigate(lessonTableUrl);
      return;
    }
    
    // Subcourse-level practice (no lessonId): go to practice tab on mobile - use subcourse-id only, no lesson-id
    if (subcourseId) {
      const basePath = fromPage?.split('?')[0] || '/learner/language-learning';
      const practiceTabUrl = `${basePath}?subcourse-id=${subcourseId}`;
      console.log('📖 ResultPage: Navigating back to practice tab (subcourse level):', practiceTabUrl);
      navigate(practiceTabUrl);
      return;
    }
    
    // Priority 1: Use fromPage from location state (from English Language Learning Page)
    if (fromPage && fromPage !== '/learner/language-learning') {
      console.log('📖 ResultPage: Navigating back to fromPage:', fromPage);
      navigate(fromPage);
      return;
    }
    
    // Priority 2: Navigate back to last opened URL
    const lastOpenedUrl = getLastOpenedUrl();
    if (lastOpenedUrl) {
      console.log('📖 ResultPage: Navigating back to lastOpenedUrl:', lastOpenedUrl);
      navigate(lastOpenedUrl);
      return;
    }
    
    // Priority 3: Fallback to fromPage or default
    console.log('📖 ResultPage: No lastOpenedUrl found, using fromPage:', fromPage);
    navigate(fromPage);
  };

  const handleContinue = () => {
    if (practiceParams) {
      if (courseType === 'Writing Practice') {
        navigate('/writing-practice', { state: practiceParams });
      } else if (courseType === 'Speaking Practice') {
        navigate('/speaking-practice', { state: practiceParams });
      } else if (courseType === 'Course Practice' || courseType === 'Quick Practice') {
        navigate('/quick-practice', { state: practiceParams });
      } else if (courseType === 'Drill Vocab') {
        // changed by Cursor: restart vocab drill with same subcourseId + fromPage
        navigate('/drill-vocab', { state: { ...practiceParams, fromPage } });
      } else {
        navigate('/learner/language-learning');
      }
    } else {
      navigate('/learner/language-learning');
    }
  };

  const handleReview = () => {
    // Combine original questions (with options) with soals-stats (with answers)
    const soalsStats = toDisplay['soals-stats'] || [];
    
    console.log('📊 Review Page - soals-stats:', soalsStats);
    console.log('📊 Review Page - originalQuestions:', originalQuestions);
    console.log('📊 Review Page - userAnswers:', userAnswers);
    
    // DEBUG: Log first soal to see structure
    if (soalsStats.length > 0) {
      console.log('🔍 First soal structure:', soalsStats[0]);
      console.log('🔍 First soal keys:', Object.keys(soalsStats[0]));
    }
    if (userAnswers.length > 0) {
      console.log('🔍 First userAnswer:', userAnswers[0]);
    }
    
    // If no soals-stats, use originalQuestions as fallback
    // FIX: Handle duplicate entries dari report soal - gunakan unique soal-id
    const questionsToProcess = soalsStats.length > 0 
      ? (() => {
          // Build array of unique soals-stats (handle duplicates from report soal)
          const uniqueSoalsStats = [];
          const seenSoalIds = new Set();
          
          soalsStats.forEach((soal) => {
            // changed by Cursor: also check drill-id for vocab drill soals-stats
            const soalId = soal['soal-id'] || soal.soalId || soal['drill-id'] || soal.id;
            // Only add if belum pernah dilihat (prevent duplicates)
            if (!soalId || !seenSoalIds.has(soalId)) {
              if (soalId) {
                seenSoalIds.add(soalId);
              }
              uniqueSoalsStats.push(soal);
            }
          });
          
          // Limit to original questions count (not soals-stats count which may have duplicates)
          return uniqueSoalsStats.slice(0, originalQuestions.length);
        })()
      : originalQuestions;
    
    const formattedQuestions = questionsToProcess.map((soal, index) => {
      // Find matching original question by soal-id or by index
      // changed by Cursor: also check drill-id for vocab drill soals-stats
      const soalId = soal['soal-id'] || soal.soalId || soal['drill-id'] || soal.id;
      let originalQuestion = originalQuestions.find(q => 
        (q['soal-id'] || q.soalId || q['drill-id'] || q.id) === soalId
      );
      
      // Fallback to index matching if soal-id doesn't match
      if (!originalQuestion && originalQuestions[index]) {
        originalQuestion = originalQuestions[index];
      }
      
      // DEBUG: Log raw soal data
      console.log(`🔍 Soal ${index + 1} raw data:`, soal);
      console.log(`🔍 Soal ${index + 1} answers field:`, soal.answers);
      console.log(`🔍 Soal ${index + 1} userAnswer from array:`, userAnswers[index]);
      
      // Handle different data structures from backend
      // Correct answer dari soals-stats.answers (dari SUBMIT API) atau dari originalQuestion jika tidak ada soals-stats
      const correctAnswer = soalsStats.length > 0
        ? (soal.answers || soal['correct-answer'] || soal.correct_answer || soal.correctAnswer)
        : (originalQuestion?.answers || originalQuestion?.['correct-answer'] || originalQuestion?.correct_answer || originalQuestion?.correctAnswer);
      
      // User answer dari userAnswers array (BUKAN dari soals-stats!)
      const userAnswerData = userAnswers[index];
      
      // DEBUG: Log userAnswerData for pronunciation
      if ((soal.type || originalQuestion?.type || '').includes('pronun')) {
        console.log(`🔍 Pronunciation ${index + 1} - userAnswerData:`, userAnswerData);
        console.log(`🔍 Pronunciation ${index + 1} - userAnswerData.assessment:`, userAnswerData?.assessment);
      }
      
      // Accuracy dari soals-stats (atau dari userAnswer jika tidak ada soals-stats)
      const isCorrect = soalsStats.length > 0 
        ? (soal.accuracy === 100 || soal.accuracy === 1.0 || (soal['is-correct'] ?? soal.is_correct ?? soal.isCorrect ?? false))
        : (userAnswerData?.isCorrect ?? false); // Fallback to userAnswer.isCorrect if no soals-stats
      
      // Get options from original question
      const rawOptions = originalQuestion?.options || originalQuestion?.choices || [];
      const questionText = originalQuestion?.['text-soal'] || originalQuestion?.text || originalQuestion?.question || soal.question || soal.prompt || `Question ${index + 1}`;
      
      // Extract option text from objects if needed
      // Options can be: [{idx, correct, "option-text"}] or ["text1", "text2"]
      const options = rawOptions.map(opt => {
        if (typeof opt === 'string') {
          return opt;
        } else if (opt && typeof opt === 'object') {
          return opt['option-text'] || opt.text || opt.optionText || String(opt);
        }
        return String(opt);
      });
      
      // Format correct answer and user answer based on type
      let formattedCorrectAnswer = [];
      let formattedUserAnswer = [];
      
      const questionType = soal.type || originalQuestion?.type || '';
      
      // Extract userAnswer based on question type
      // For pronunciation/speaking, keep full object to preserve assessment data
      let userAnswer;
      if (questionType === 'pronunciation' || questionType === 'speaking') {
        userAnswer = userAnswerData; // Keep full object
      } else {
        // CRITICAL: Untuk pairing, ambil dari 'user-answer' field (format: {left: [...], right: [...]})
        // Fallback ke 'answer' atau userAnswerData langsung
        userAnswer = userAnswerData?.['user-answer'] ?? userAnswerData?.userAnswer ?? userAnswerData?.answer ?? userAnswerData;
      }
      
      // Debug: log raw userAnswer before formatting (after questionType is defined)
      if (questionType.includes('fib') || questionType.includes('fill')) {
        console.log(`🔍 FIB Question ${index + 1} - Raw userAnswer:`, {
          userAnswerData,
          userAnswer,
          userAnswerType: typeof userAnswer,
          isArray: Array.isArray(userAnswer),
          userAnswerValues: Array.isArray(userAnswer) ? userAnswer : null,
        });
      }
      
      console.log(`📊 Question ${index + 1}:`, {
        questionType,
        correctAnswer,
        userAnswer,
        correctAnswerType: typeof correctAnswer,
        userAnswerType: typeof userAnswer,
        isUserAnswerArray: Array.isArray(userAnswer),
        options: options.length,
      });
      
      if (questionType === 'sequencing' || questionType === 'Sequencing') {
        // For sequencing: correctAnswer and userAnswer are already ordered arrays
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : [];
        formattedUserAnswer = Array.isArray(userAnswer) ? userAnswer : [];
      } else if (questionType === 'pairing' || questionType === 'pair') {
        // For pairing: userAnswer should be {left: [...], right: [...]}
        // If it's already an object, use as is; if array, keep as array (will be handled in Pairing.jsx)
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : [];
        formattedUserAnswer = userAnswer; // Keep original format (object or array)
      } else if (questionType.includes('pilgan') || questionType.includes('sc') || questionType.includes('mc')) {
        // For multiple choice: map indices to option text
        if (Array.isArray(correctAnswer)) {
          formattedCorrectAnswer = correctAnswer.map(idx => options[idx] || `Option ${idx}`);
        } else if (typeof correctAnswer === 'number' && options[correctAnswer]) {
          formattedCorrectAnswer = [options[correctAnswer]];
        }
        
        if (Array.isArray(userAnswer)) {
          formattedUserAnswer = userAnswer.map(idx => options[idx] || `Option ${idx}`);
        } else if (typeof userAnswer === 'number' || userAnswer === 0) {
          // Single index - convert to array with option text (0 is valid!)
          formattedUserAnswer = options[userAnswer] !== undefined ? [options[userAnswer]] : [`Option ${userAnswer}`];
        }
      } else if (questionType.includes('fib') || questionType.includes('fill')) {
        // For fill in the blank: keep as idx array in order of blanks in text
        // userAnswer is array of idx like [3, 1, 2] where index corresponds to blank order in text
        // Example: blank-1 gets idx 3, blank-2 gets idx 1, blank-3 gets idx 2
        // Don't convert to text here because options array might not match choicesMap structure
        if (Array.isArray(correctAnswer)) {
          formattedCorrectAnswer = correctAnswer; // Keep as idx array [1, 2, 3] or [3, 1, 2] etc
        }
        if (Array.isArray(userAnswer)) {
          // Keep as idx array, preserve order - each index corresponds to blank order in text
          // Ensure all values are numbers (idx), convert if needed
          formattedUserAnswer = userAnswer.map((val, idx) => {
            // If already a number, use it (this is the idx from choices)
            if (typeof val === 'number') {
              return val;
            }
            // If string that can be parsed as number, parse it
            if (typeof val === 'string' && !isNaN(Number(val)) && val.trim() !== '') {
              return parseInt(val, 10);
            }
            // If text, try to find idx in options (fallback, should not happen if data is correct)
            const foundIdx = options.findIndex(opt => opt === val);
            if (foundIdx !== -1) {
              return foundIdx;
            }
            // Last resort: return as-is (should not happen)
            console.warn(`⚠️ FIB: Could not convert value at index ${idx} to idx:`, val);
            return val;
          });
        } else if (typeof userAnswer === 'number' || userAnswer === 0) {
          formattedUserAnswer = [userAnswer]; // Convert single number to array
        }
      } else if (questionType === 'pronunciation') {
        // For pronunciation: extract assessment result from multiple sources
        // Priority: userAnswerData.assessment > soal['assessment-result'] > localStorage
        let assessmentResult = userAnswerData?.assessment || 
                                soal['assessment-result'] || 
                                soal.assessment_result || 
                                soal.assessmentResult;
        
        // If still no assessment, try to get from localStorage
        // Try all possible soalIds (same as audio search logic)
        if (!assessmentResult) {
          try {
            // Extract all possible soalIds to match (same as audio search)
            const possibleSoalIds = [
              soalId,
              originalQuestion?.['soal-id'],
              originalQuestion?.id,
              originalQuestion?.soalId,
            ].filter(Boolean);
            
            // Try each possible soalId
            for (const qId of possibleSoalIds) {
              if (!qId) continue;
              const savedAssessmentKey = `pronunciation-assessment-${qId}`;
              const savedAssessment = localStorage.getItem(savedAssessmentKey);
              if (savedAssessment) {
                assessmentResult = JSON.parse(savedAssessment);
                console.log(`📦 Loaded assessment from localStorage for pronunciation ${index + 1} by soal-id:`, qId);
                break;
              }
            }
            
            // If still not found, try to find any assessment with matching soal-id pattern
            if (!assessmentResult) {
              try {
                const allKeys = Object.keys(localStorage);
                const assessmentKeys = allKeys.filter(key => key.startsWith('pronunciation-assessment-'));
                // changed by Cursor: gunakan String() conversion untuk hindari type mismatch
                const possibleIdsAsString = possibleSoalIds.map(String);
                for (const key of assessmentKeys) {
                  const keySoalId = key.replace('pronunciation-assessment-', '');
                  if (possibleIdsAsString.includes(keySoalId)) {
                    const savedAssessment = localStorage.getItem(key);
                    if (savedAssessment) {
                      assessmentResult = JSON.parse(savedAssessment);
                      console.log(`📦 Loaded assessment from localStorage for pronunciation ${index + 1} by matching key:`, key);
                      break;
                    }
                  }
                }
              } catch (err) {
                console.error('Error searching assessment keys:', err);
              }
            }
          } catch (err) {
            console.error('Error loading assessment from localStorage:', err);
          }
        }
        
        // changed by Cursor: read audio from Recoil instead of localStorage (reliable, no quota issues)
        let audioUrl = userAnswerData?.audioUrl || null;
        if (!audioUrl) {
          const possibleSoalIds = [soalId, originalQuestion?.['soal-id'], originalQuestion?.id, originalQuestion?.soalId].filter(Boolean);
          const possibleIdsAsString = possibleSoalIds.map(String);
          for (const [, rec] of Object.entries(audioRecordings)) {
            if (rec?.soalId && possibleIdsAsString.includes(String(rec.soalId))) {
              audioUrl = rec.audioUrl;
              break;
            }
          }
          if (!audioUrl) {
            audioUrl = audioRecordings[index.toString()]?.audioUrl || null;
          }
        }
        
        console.log(`🔍 Pronunciation ${index + 1} - Data extraction:`, {
          soalId,
          possibleSoalIds: [
            soalId,
            originalQuestion?.['soal-id'],
            originalQuestion?.id,
            originalQuestion?.soalId,
          ].filter(Boolean),
          'soal.assessment-result': !!soal['assessment-result'],
          'userAnswerData.assessment': !!userAnswerData?.assessment,
          'finalAssessmentResult': !!assessmentResult,
          assessmentResultKeys: assessmentResult ? Object.keys(assessmentResult) : [],
          audioUrl: audioUrl ? audioUrl.substring(0, 50) : null,
          'userAnswerData.userAnswer': userAnswerData?.userAnswer,
        });
        
        // For pronunciation, userAnswer should include assessment and audioUrl
        formattedUserAnswer = {
          assessment: assessmentResult,
          audioUrl: audioUrl,
          text: userAnswerData?.userAnswer || userAnswerData?.text || originalQuestion?.['text-to-read'] || originalQuestion?.text,
        };
        formattedCorrectAnswer = []; // No correct answer for pronunciation
        
        console.log(`✅ Pronunciation ${index + 1} - Formatted:`, {
          soalId,
          hasAssessment: !!formattedUserAnswer.assessment,
          assessmentKeys: formattedUserAnswer.assessment ? Object.keys(formattedUserAnswer.assessment) : [],
          hasAudio: !!formattedUserAnswer.audioUrl,
          text: formattedUserAnswer.text?.substring(0, 50),
        });
        
        // Warn if assessment is missing
        if (!formattedUserAnswer.assessment) {
          console.warn(`⚠️ Pronunciation ${index + 1} - Assessment missing!`, {
            soalId,
            possibleSoalIds: [
              soalId,
              originalQuestion?.['soal-id'],
              originalQuestion?.id,
              originalQuestion?.soalId,
            ].filter(Boolean),
            allAssessmentKeys: Object.keys(localStorage).filter(k => k.startsWith('pronunciation-assessment-'))
          });
        }
      } else if (questionType === 'speaking') {
        // changed by Cursor: read audio from Recoil instead of localStorage
        let audioUrl = audioRecordings[index.toString()]?.audioUrl || null;
        if (!audioUrl) {
          for (const [, rec] of Object.entries(audioRecordings)) {
            if (rec?.soalId && String(rec.soalId) === String(soalId)) {
              audioUrl = rec.audioUrl;
              break;
            }
          }
        }
        
        // For speaking, userAnswer includes audioUrl and text only (no assessment)
        formattedUserAnswer = {
          audioUrl: audioUrl,
          text: soal['user-answer'] || userAnswerData?.userAnswer || originalQuestion?.text,
        };
        formattedCorrectAnswer = []; // No correct answer for speaking
        
        console.log(`✅ Speaking ${index + 1} - Formatted:`, formattedUserAnswer);
      } else {
        // Default: use as is
        formattedCorrectAnswer = Array.isArray(correctAnswer) ? correctAnswer : (correctAnswer !== undefined ? [correctAnswer] : []);
        formattedUserAnswer = Array.isArray(userAnswer) ? userAnswer : (userAnswer !== undefined ? [userAnswer] : []);
      }
      
      console.log(`📊 Question ${index + 1} formatted:`, {
        formattedCorrectAnswer,
        formattedUserAnswer,
      });
      
      // changed by Cursor: include passage & translation from sessionData (session-level) for passage type review
      const reviewQuestion = {
        type: questionType,
        originalQuestion: originalQuestion, // Pass full original question for component rendering
        prompt: questionText,
        audioUrl: originalQuestion?.['audio-url'] || originalQuestion?.audio_url || soal['audio-url'] || soal.audio_url,
        options: options, // Include options for reference
        correctAnswer: formattedCorrectAnswer,
        userAnswer: formattedUserAnswer,
        isCorrect,
        points: soal.points || (isCorrect ? 100 : 0),
        showIndices: questionType === 'sequencing' || questionType === 'Sequencing',
        correctIndices: soal['correct-indices'] || soal.correct_indices,
        userIndices: soal['user-indices'] || soal.user_indices,
        explanation: soal.explanation || originalQuestion?.explanation || originalQuestion?.notes,
        translation: soal.translation || originalQuestion?.translation || sessionData?.translation,
        passage: originalQuestion?.passage || sessionData?.passage,
        feedback: soal.feedbacks || soal.feedback, // Add feedback from soals-stats
        accuracy: soal.accuracy, // Add accuracy from soals-stats
      };
      
      // Debug: Log for pronunciation type
      if (questionType === 'pronunciation') {
        console.log(`🔍 ResultPage - Question ${index + 1} built:`, {
          'reviewQuestion.userAnswer': reviewQuestion.userAnswer,
          'userAnswer type': typeof reviewQuestion.userAnswer,
          'userAnswer keys': typeof reviewQuestion.userAnswer === 'object' ? Object.keys(reviewQuestion.userAnswer) : 'not object',
        });
      }
      
      return reviewQuestion;
    });

    console.log('📊 Review Page - formatted questions:', formattedQuestions);

    // changed by Cursor: vocab drill → vocab-review page (blue colors)
    const reviewRoute = isDrillVocab ? '/learner/vocab-review' : '/learner/review';
    navigate(reviewRoute, {
      state: {
        courseType,
        score,
        currentLevel,
        questions: formattedQuestions,
        userAnswers, // Pass user answers for reference
        fromPage: '/learner/result',
        apiResponse, // Pass full API response for debugging
        // Store all result page data to restore when going back
        resultPageState: {
          language,
          courseType,
          level,
          currentLevel,
          previousLevel,
          deltaLevel,
          score,
          totalQuestions,
          correctCount,
          practiceParams,
          apiResponse,
          originalQuestions,
          userAnswers,
          sessionData,
          fromPage,
        },
      },
    });
  };

  // Don't render if no data (will redirect via useEffect)
  // Allow rendering even if apiResponse is null, as long as we have minimal data
  if (!locationState) {
    return null;
  }
  
  // Check if we have minimal required data (either apiResponse OR fallback data)
  const hasApiResponse = !!locationState.apiResponse;
  const hasFallbackData = locationState.score !== undefined || 
                          locationState.totalQuestions !== undefined ||
                          locationState.correctCount !== undefined;
  
  if (!hasApiResponse && !hasFallbackData) {
    return null;
  }

  const subcourseId = practiceParams?.subcourseId || sessionData?.['subcourse-id'];
  const cachedLessons = subcourseId ? lessonsCache[subcourseId]?.lessons || [] : [];
  const { subcourseName, lessonName, practiceTypeLabel, subPracticeTypeLabel, subpracticeTypeLabel } = getResultPracticeContext({
    sessionData,
    practiceParams,
    courseType,
    level,
    lessons: cachedLessons,
    courseTree,
    courseTreeBaseline,
    originalQuestions,
  });

  return (
    <ResultPage
      language={language}
      courseType={courseType}
      level={level}
      subcourseName={subcourseName}
      lessonName={lessonName}
      practiceTypeLabel={practiceTypeLabel}
      subPracticeTypeLabel={subPracticeTypeLabel}
      subpracticeTypeLabel={subpracticeTypeLabel}
      currentLevel={currentLevel}
      previousLevel={previousLevel}
      deltaLevel={deltaLevel}
      score={score}
      totalQuestions={totalQuestions}
      correctCount={correctCount}
      onBack={handleBack}
      onContinue={handleContinue}
      onReview={handleReview}
    />
  );
};

export { ResultPageRoute };

export default ResultPageRoute;

